import React from 'react'
import {
  PlusIcon,
  MinusIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
} from "@heroicons/react/24/outline"

import { 
  ClockIcon, 
  CalendarDaysIcon, 
  UserGroupIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  ShareIcon,
  CheckCircleIcon, 
} from "@heroicons/react/24/solid"

// 01 Apr 2026
import moment from 'moment'

const EtHabitPerformanceDetail = ({
  habit,
  userHabitLogTodayMap,
  performedToggle,
  timePicker,
  experienceNotes,
  defaultTime,
  setPerformedToggle,
  setTimePicker,
  setExperienceNotes,
  handleLogIt,
  handleUpdateHabitLog, // 01 Apr 2026
}) => {

  // First Created: 01 Jul 2025
  // Author  - Atul Pandey
  // Purpose - Show detailed performance input controls for a single habit (toggle, time, note, log button)
  // Updated - 02 Jul 2025 - Refined the JSX
  // Updated - 16 Dec 2025 - Refined the Time picker - made it more flexible
  // Updated - 01 Apr 2026 - Refined the logic for edit

  // 1 - Variables
  const habitId = habit._id
  const existingLog = userHabitLogTodayMap[habitId]
  const isLogged = !!existingLog

  // 01 Apr 2026 - allow edit only for logs from today
  const canEditLogged = isLogged && existingLog?.performedAt
    ? moment(existingLog.performedAt).isSame(moment(), 'day')
    : false

  const [isEditing, setIsEditing] = React.useState(false) // 01 Apr 2026

  // 2a - Local Time for time picker - 16 Dec 2025
  const getLocalTimeHHMM = () => {
    const now = new Date()
    return now.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  // 2b - Auto-initialize time on first render - 16 Dec 2025 

  // 01 Apr 2026
  React.useEffect(() => {
    // 1 - Preload time
    if (!timePicker[habitId]) {
      // A - If already logged today, preload original logged time
      if (isLogged && existingLog?.performedAt) {
        const performedDate = new Date(existingLog.performedAt)
        const hh = String(performedDate.getHours()).padStart(2, '0')
        const mm = String(performedDate.getMinutes()).padStart(2, '0')

        setTimePicker((prev) => ({
          ...prev,
          [habitId]: `${hh}:${mm}`,
        }))
      }
      // B - If not logged yet today, preload default time
      else if (!isLogged && defaultTime) {
        const parsedDefaultTime = moment(defaultTime, ['h:mm A', 'HH:mm'])
        const hh = parsedDefaultTime.format('HH')
        const mm = parsedDefaultTime.format('mm')

        setTimePicker((prev) => ({
          ...prev,
          [habitId]: `${hh}:${mm}`,
        }))
      }
    }

    // 2 - Preload latest note text
    if (isLogged && experienceNotes[habitId] === undefined) {
      setExperienceNotes((prev) => ({
        ...prev,
        [habitId]: existingLog?.experienceNotes?.[existingLog.experienceNotes.length - 1]?.experienceNoteText || '',
      }))
    }
  }, [
      habitId, 
      existingLog, 
      isLogged, 
      timePicker, 
      experienceNotes, 
      setTimePicker, 
      setExperienceNotes
  ])

  // React.useEffect(() => {
  //   if (!timePicker[habitId]) {
  //     setTimePicker((prev) => ({
  //       ...prev,
  //       [habitId]: getLocalTimeHHMM(),
  //     }))
  //   }
  // }, [habitId])



  return (
    <div className="col-span-12 mt-2 mb-6">

      {/* Row 1 – Performed Toggle + Time Picker */}
      {/* 01 Apr 2026 */}
      <div className="flex space-x-6 items-center">
        {/* Performed Toggle */}
        {isLogged ? (
          <div className="flex items-center">
            <span className="text-orange-600 font-bold mx-1 px-1">Performed!</span>
            <div className="w-12 h-6 flex items-center rounded-full p-1 bg-lime-400">
              <div className="bg-white w-4 h-4 rounded-full shadow-md transform translate-x-6" />
            </div>
          </div>
        ) : (
          <div className="flex items-center">
            <span className="text-xs text-gray-600 mr-2">Performed?</span>
            <div
              className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors duration-300 cursor-pointer ${
                performedToggle[habitId] ? 'bg-lime-400' : 'bg-gray-200'
              }`}
              onClick={() =>
                setPerformedToggle((prev) => ({
                  ...prev,
                  [habitId]: !prev[habitId],
                }))
              }
            >
              <div
                className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${
                  performedToggle[habitId] ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </div>
          </div>
        )}

        {/* Time Picker */}
        <div className="flex items-center space-x-2">
          <ClockIcon className="h-7 w-7 text-lime-600" />
          {isLogged && !isEditing ? (
            <p className="text-gray-500 ml-1 px-2 rounded-full bg-orange-100 text-sm">
              {new Date(existingLog?.performedAt).toLocaleString('en-US', {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })}
            </p>
          ) : (
            <input
              type="time"
              value={timePicker[habitId] || ''}
              onChange={(e) =>
                setTimePicker((prev) => ({
                  ...prev,
                  [habitId]: e.target.value,
                }))
              }
              className="border border-gray-300 px-2 py-1 rounded text-sm"
            />
          )}
        </div>

      </div>



      {/* Prior Code */}
      {/* <div className="flex space-x-6 items-center">
        
        {isLogged ? (
            <div className="flex items-center">
                <span className="text-orange-600 font-bold mx-1 px-1">Performed!</span>
                <div className="w-12 h-6 flex items-center rounded-full p-1 bg-lime-400">
                <div className="bg-white w-4 h-4 rounded-full shadow-md transform translate-x-6" />
                </div>
            </div>
            ) : (
                <div className="flex items-center">
                    <span className="text-xs text-gray-600 mr-2">Performed?</span>
                    <div
                        className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors duration-300 cursor-pointer ${
                        performedToggle[habitId] ? 'bg-lime-400' : 'bg-gray-200'
                        }`}
                        onClick={() =>
                        setPerformedToggle((prev) => ({
                            ...prev,
                            [habitId]: !prev[habitId],
                        }))
                        }
                    >
                        <div
                        className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${
                            performedToggle[habitId] ? 'translate-x-6' : 'translate-x-0'
                        }`}
                        />
                    </div>
                </div>
        )}

        
        <div className="flex items-center space-x-2">
            <ClockIcon className="h-7 w-7 text-lime-600" />
            {isLogged ? (
                <p className="text-gray-500 ml-1 px-2 rounded-full bg-orange-100 text-sm">
                {new Date(habit?.performedAt || habit?.performed_at || habit?.performedTime || habit?.performed_time || existingLog?.performedAt).toLocaleString('en-US', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                })}
                </p>
            ) : (
                // <select
                // value={timePicker[habitId] || defaultTime}
                // onChange={(e) =>
                //     setTimePicker((prev) => ({
                //     ...prev,
                //     [habitId]: e.target.value,
                //     }))
                // }
                // className="border border-gray-300 px-2 py-1 rounded text-sm"
                // >
                // <option value="">Now</option>
                // {Array.from({ length: 24 * 2 }, (_, i) => {
                //     const hour = Math.floor(i / 2)
                //     const minute = i % 2 === 0 ? '00' : '30'
                //     const timeStr = `${(hour % 12 || 12)
                //     .toString()
                //     .padStart(2, '0')}:${minute} ${hour < 12 ? 'AM' : 'PM'}`
                //     return (
                //     <option key={timeStr} value={timeStr}>
                //         {timeStr}
                //     </option>
                //     )
                // })}
                // </select>
                <input
                  type="time"
                  value={timePicker[habitId]}
                  onChange={(e) =>
                    setTimePicker((prev) => ({
                      ...prev,
                      [habitId]: e.target.value,
                    }))
                  }
                  className="border border-gray-300 px-2 py-1 rounded text-sm"
                />

            )}
        </div>

      </div> */}

      {/* Row 2 – Experience Notes */}
      {/* 01 Apr 2026 */}
      <div className="mt-3">
        {isLogged && !isEditing ? (
          // 01 Apr 2026
          <div>
            <div className="flex items-center justify-between m-1 p-1">
              <p className="text-sm font-semibold text-gray-700">Experience Notes</p>

              {canEditLogged && (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center justify-center rounded-full p-1"
                title="Edit note"
              >
                <PencilSquareIcon className="h-4 w-4 text-orange-600" />
              </button>
              )}
            </div>

            <div className="bg-gray-50 p-2 rounded shadow mx-2">
              <p className="text-sm text-gray-800">
                {existingLog?.experienceNotes?.[existingLog.experienceNotes.length - 1]?.experienceNoteText || '—'}
              </p>
            </div>
          </div>
          // <div>
          //   <p className="text-sm font-semibold text-gray-700 m-1 p-1">Experience Notes</p>
          //   <div className="bg-gray-50 p-2 rounded shadow mx-2">
          //     <p className="text-sm text-gray-800">
          //       {existingLog?.experienceNotes?.[existingLog.experienceNotes.length - 1]?.experienceNoteText || '—'}
          //     </p>
          //   </div>
          // </div>
        ) : (
          <div>
            {/* 01 Apr 2026 */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center">
                <PencilSquareIcon className="h-4 w-4 text-gray-600 mr-1" />
                <label className="text-sm font-semibold text-gray-700">Experience Notes</label>
              </div>

              {canEditLogged && (
                <button
                  onClick={() => setIsEditing(prev => !prev)}
                  className="flex items-center justify-center rounded-full p-1"
                  title={isEditing ? 'View note' : 'Edit note'}
                >
                  {isEditing ? (
                    <EyeIcon className="h-4 w-4 text-orange-600" />
                  ) : (
                    <PencilSquareIcon className="h-4 w-4 text-orange-600" />
                  )}
                </button>
              )}
            </div>
            {/* <div className="flex items-center mb-1">
              <PencilSquareIcon className="h-4 w-4 text-gray-600 mr-1" />
              <label className="text-sm font-semibold text-gray-700">Experience Notes</label>
            </div> */}
            <textarea
              value={experienceNotes[habitId] || ''}
              onChange={(e) =>
                setExperienceNotes((prev) => ({
                  ...prev,
                  [habitId]: e.target.value,
                }))
              }
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              rows={2}
              placeholder="What did you experience?"
            />
          </div>
        )}
      </div>

      {/* Prior Code */}
      {/* <div className="mt-3">
        
        {isLogged ? (
                <div>
                    <p className="text-sm font-semibold text-gray-700 m-1 p-1">Experience Notes</p>
                    <div className="bg-gray-50 p-2 rounded shadow mx-2"> 
                        <p className="text-sm text-gray-800">
                        {existingLog?.experienceNotes?.at(-1)?.experienceNoteText || '—'}
                        </p>
                    </div>
                </div>
            ) : (
                <div>
                    <div className="flex items-center mb-1">
                        <PencilSquareIcon className="h-4 w-4 text-gray-600 mr-1" />
                        <label className="text-sm font-semibold text-gray-700">Experience Notes</label>
                    </div>
                    <textarea
                        value={experienceNotes[habitId] || ''}
                        onChange={(e) =>
                        setExperienceNotes((prev) => ({
                            ...prev,
                            [habitId]: e.target.value,
                        }))
                        }
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        rows={2}
                        placeholder="What did you experience?"
                    />
                </div> 
        )}
      </div> */}

      {/* Log Button */}
      {!isLogged && (
        <div className="mt-2">
          <button
            onClick={() => handleLogIt(habit)}
            className="bg-lime-500 text-white text-sm px-3 py-1 rounded shadow hover:bg-lime-600"
          >
            Log it!
          </button>
        </div>
      )}

      {/* 01 Apr 2026 */}
      {canEditLogged && isEditing && (
        <div className="mt-2">
          <button
            onClick={async () => {
              await handleUpdateHabitLog(habit)
              setIsEditing(false)
            }}
            className="bg-lime-600 text-white text-sm px-3 py-1 rounded shadow hover:bg-lime-700"
          >
            Save
          </button>
        </div>
      )}
    </div>
  )
}

export default EtHabitPerformanceDetail